import { useEffect, useRef, useState } from "react";
import { DollarSign, MapPin, Clock, Activity, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataTable } from "../Table";
import "./DashboardView.css";

export default function DashboardView({
  dashboardStats,
  executives,
  searchQuery,
  loading,
  onOpenSalesReports,
  selectedPromoterId,
  selectedPromoterName,
  promoterInput,
  onPromoterInputChange,
  onApplyPromoterFilter,
  onClearPromoterFilter,
  promoterOptions,
  promoterOptionsLoading,
}) {
  const { t, i18n } = useTranslation();
  const filteredExecutives = executives.filter((e) =>
    e.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const resolvedPromoterOptions = Array.isArray(promoterOptions)
    ? [...promoterOptions]
    : [];
  const getPromoterOptionValue = (promoter) =>
    promoter?.email ? `${promoter.full_name} (${promoter.email})` : promoter?.full_name || "";
  const [showPromoterSuggestions, setShowPromoterSuggestions] = useState(false);
  const [productPage, setProductPage] = useState(0);
  const promoterHelperRef = useRef(null);
  const PRODUCTS_PAGE_SIZE = 5;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!promoterHelperRef.current) return;
      if (!promoterHelperRef.current.contains(event.target)) {
        setShowPromoterSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const locale = i18n.language === "ms" ? "ms-MY" : "en-MY";

  const formatCurrency = (amount) => {
    return amount.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return t("admin.dashboard.na");
    const date = new Date(dateString);
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusClass = (statusCode) => {
    switch (statusCode) {
      case 'active': return 'admin-status-active';
      case 'checked_out': return 'admin-status-checked-out';
      default: return 'admin-status-idle';
    }
  };

  const getStatusKey = (status) => {
    const normalized = status?.toLowerCase();
    if (normalized === "active") return "active";
    if (normalized === "checked out") return "checked_out";
    return "idle";
  };

  const getCheckoutReasonKey = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "lunch" || normalized === "break") return "break";
    if (normalized === "off_duty") return "off_duty";
    if (normalized === "auto_checkout") return "auto_checkout";
    return null;
  };

  const toBooleanFlag = (value, fallbackText = null) => {
    if (value === true || value === false) return value;
    if (typeof value === "number") return value > 0;
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "1" || normalized === "true" || normalized === "t") return true;
    if (normalized === "0" || normalized === "false" || normalized === "f") return false;
    return String(fallbackText || "").trim().toLowerCase() === "roadshow";
  };

  const shouldShowRoadshowDetails = (details) => {
    const normalizedDetails = String(details || "").trim();
    if (!normalizedDetails) return false;
    return normalizedDetails.toLowerCase() !== "roadshow";
  };

  const selectedStatusIsActive = dashboardStats.status === "active";
  const statusLabel = selectedStatusIsActive
    ? t("admin.dashboard.activeStatus")
    : t("admin.dashboard.notActiveStatus");
  const hasPromoterSuggestions = resolvedPromoterOptions.length > 0;
  const showSuggestionDropdown = showPromoterSuggestions;
  const products = dashboardStats.productsSoldToday || dashboardStats.topProducts || [];
  const productsTotalPages = Math.max(1, Math.ceil(products.length / PRODUCTS_PAGE_SIZE));
  const paginatedProducts = products.slice(
    productPage * PRODUCTS_PAGE_SIZE,
    productPage * PRODUCTS_PAGE_SIZE + PRODUCTS_PAGE_SIZE,
  );

  useEffect(() => {
    setProductPage(0);
  }, [selectedPromoterId, products.length]);

  useEffect(() => {
    if (productPage > productsTotalPages - 1) {
      setProductPage(Math.max(productsTotalPages - 1, 0));
    }
  }, [productPage, productsTotalPages]);

  return (
    <div className="admin-dashboard-content">
      <div className="admin-dashboard-filter-card">
        <div className="admin-dashboard-filter-head">
          <div className="admin-dashboard-filter-title">
            {t("admin.dashboard.promoterFilter")}
          </div>
          {selectedPromoterId && (
            <div className="admin-dashboard-filter-subtitle">
              {selectedPromoterName || t("admin.dashboard.filteredPromoter")}
            </div>
          )}
        </div>
        <div className="admin-dashboard-filter-controls">
          <div className="admin-dashboard-filter-helper" ref={promoterHelperRef}>
            <input
              type="text"
              value={promoterInput}
              onChange={(event) => {
                onPromoterInputChange?.(event.target.value);
                setShowPromoterSuggestions(true);
              }}
              onFocus={() => setShowPromoterSuggestions(true)}
              placeholder={t("admin.dashboard.searchPromoter")}
              className="admin-dashboard-filter-search"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  setShowPromoterSuggestions(false);
                  onApplyPromoterFilter?.();
                }
                if (event.key === "Escape") {
                  setShowPromoterSuggestions(false);
                }
              }}
              autoComplete="off"
            />
            {showSuggestionDropdown && (
              <div className="admin-dashboard-suggestions">
                {promoterOptionsLoading ? (
                  <div className="admin-dashboard-suggestion-empty">
                    {t("admin.common.loading")}
                  </div>
                ) : hasPromoterSuggestions ? (
                  resolvedPromoterOptions.map((promoter) => {
                    const optionValue = getPromoterOptionValue(promoter);
                    return (
                      <button
                        key={promoter.user_id}
                        type="button"
                        className="admin-dashboard-suggestion-item"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          onPromoterInputChange?.(optionValue);
                          onApplyPromoterFilter?.(optionValue);
                          setShowPromoterSuggestions(false);
                        }}
                      >
                        {optionValue}
                      </button>
                    );
                  })
                ) : (
                  <div className="admin-dashboard-suggestion-empty">
                    {t("admin.dashboard.noPromoterMatches")}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="admin-dashboard-filter-btn is-primary"
            onClick={() => onApplyPromoterFilter?.()}
          >
            {t("admin.common.apply")}
          </button>
          <button
            type="button"
            className="admin-dashboard-filter-btn"
            onClick={() => onClearPromoterFilter?.()}
          >
            {t("admin.common.clear")}
          </button>
        </div>
        <p className="admin-dashboard-filter-hint">
          {t("admin.dashboard.promoterFilterHint")}
        </p>
      </div>

      <div className="admin-stats-grid">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={<DollarSign size={24} color="#f59e0b" />}
              label={t("admin.dashboard.todaySales")}
              value={`RM ${formatCurrency(dashboardStats.totalSales)}`}
              bg="#fef3c7"
            />

            <StatCard
              icon={<Activity size={24} color={selectedStatusIsActive ? "#10b981" : "#ef4444"} />}
              label={t("admin.dashboard.statusIndicator")}
              value={
                <span
                  className={`admin-status-indicator-text ${selectedStatusIsActive ? "is-active" : "is-inactive"}`}
                >
                  {statusLabel}
                </span>
              }
              bg={selectedStatusIsActive ? "#d1fae5" : "#fee2e2"}
            />

            <StatCard
              icon={<MapPin size={24} color="#a855f7" />}
              label={t("admin.dashboard.visitsToday")}
              value={dashboardStats.visitsToday}
              bg="#e9d5ff"
            />
          </>
        )}
      </div>

      <div className="admin-top-products-card">
        <div className="admin-table-header">
          <h2 className="admin-table-title">{t("admin.dashboard.productsSoldToday")}</h2>
          <div className="admin-top-products-header-actions">
            <div className="admin-top-products-nav">
              <button
                type="button"
                className="admin-top-products-nav-btn"
                aria-label="Previous products page"
                disabled={loading || productPage === 0}
                onClick={() => setProductPage((prev) => Math.max(prev - 1, 0))}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                className="admin-top-products-nav-btn"
                aria-label="Next products page"
                disabled={loading || productPage >= productsTotalPages - 1}
                onClick={() =>
                  setProductPage((prev) => Math.min(prev + 1, productsTotalPages - 1))
                }
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <button
              type="button"
              className="admin-link-btn"
              onClick={() => onOpenSalesReports?.("")}
            >
              {t("admin.dashboard.viewSalesReports")}
            </button>
          </div>
        </div>
        <div className="admin-top-products-list">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div className="admin-top-product-row" key={`top-product-skeleton-${i}`}>
                <div className="dashboard-skeleton-text" style={{ width: "45%" }}></div>
                <div className="dashboard-skeleton-text" style={{ width: "12%" }}></div>
                <div className="dashboard-skeleton-text" style={{ width: "20%" }}></div>
              </div>
            ))
          ) : products.length > 0 ? (
            paginatedProducts.map((product, index) => (
              <div className="admin-top-product-row" key={`${product.productName}-${index}`}>
                <div className="admin-top-product-name">
                  {product.productName}
                  {product.productCode ? ` (${product.productCode})` : ""}
                </div>
                <div className="admin-top-product-qty">{product.quantity} pcs</div>
                <div className="admin-top-product-revenue">
                  RM {formatCurrency(product.revenue)}
                </div>
                <button
                  type="button"
                  className="admin-link-btn"
                  onClick={() => onOpenSalesReports?.(product.productName)}
                >
                  {t("admin.dashboard.drillDown")}
                </button>
              </div>
            ))
          ) : (
            <div className="admin-top-products-empty">{t("admin.dashboard.noProductSales")}</div>
          )}
        </div>
      </div>

      <div className="admin-table-container">
        <DataTable
          data={filteredExecutives}
          columns={[
            {
              label: t("admin.dashboard.name"),
              sortKey: "name",
              sortValue: (executive) => String(executive?.name || "").toLowerCase(),
            },
            {
              label: t("admin.common.status"),
              sortKey: "status",
              sortValue: (executive) => {
                const statusOrder = {
                  active: 0,
                  checked_out: 1,
                  idle: 2,
                };
                return statusOrder[getStatusKey(executive?.status)] ?? 99;
              },
            },
            {
              label: t("admin.dashboard.location"),
              sortKey: "location",
              sortValue: (executive) => String(executive?.location || "").toLowerCase(),
            },
            {
              label: t("admin.dashboard.checkInTime"),
              sortKey: "checkedInAt",
              sortValue: (executive) => {
                const value = new Date(executive?.checkedInAt).getTime();
                return Number.isFinite(value) ? value : 0;
              },
            },
            {
              label: t("admin.dashboard.checkOutTime"),
              sortKey: "checkedOutAt",
              sortValue: (executive) => {
                const value = new Date(executive?.checkedOutAt).getTime();
                return Number.isFinite(value) ? value : -1;
              },
            },
            {
              label: t("admin.sessions.checkoutReason.label"),
              sortKey: "checkoutReason",
              sortValue: (executive) =>
                String(executive?.checkoutReason || executive?.checkout_reason || "").toLowerCase(),
            },
          ]}
          renderRow={(executive, index) => {
            const statusKey = getStatusKey(executive.status);
            const checkoutReasonKey = getCheckoutReasonKey(
              executive.checkoutReason || executive.checkout_reason,
            );
            const isRoadshow = toBooleanFlag(
              executive.isEvent || executive.is_event,
              executive.eventDetails || executive.event_details,
            );
            const roadshowDetails = String(
              executive.eventDetails || executive.event_details || "",
            ).trim();
            return (
              <tr key={`${executive.id || "executive"}-${executive.checkedInAt || index}`}>
                <td className="admin-table-name">{executive.name || t("admin.dashboard.na")}</td>
                <td>
                  <span className={`admin-status-badge ${getStatusClass(statusKey)}`}>
                    {statusKey === "active"
                      ? t("admin.common.active")
                      : statusKey === "checked_out"
                        ? t("admin.dashboard.checkedOut")
                        : t("admin.dashboard.idle")}
                  </span>
                </td>
                <td>
                  <div className="admin-table-location-stack">
                    <div className="admin-table-location">
                      <MapPin size={16} />
                      <span>{executive.location || t("admin.dashboard.na")}</span>
                    </div>
                    <span
                      className={`admin-dashboard-roadshow-badge ${isRoadshow ? "is-roadshow" : "is-outlet"}`}
                    >
                      {isRoadshow
                        ? t("admin.sessions.roadshow.label")
                        : t("admin.sessions.outletCheckIn")}
                    </span>
                    {isRoadshow && shouldShowRoadshowDetails(roadshowDetails) ? (
                      <span className="admin-dashboard-roadshow-details">{roadshowDetails}</span>
                    ) : null}
                  </div>
                </td>
                <td>
                  <div className="admin-table-time">
                    <Clock size={14} />
                    <span>{formatTime(executive.checkedInAt)}</span>
                  </div>
                </td>
                <td>
                  <div className="admin-table-time">
                    <Clock size={14} />
                    <span className={!executive.checkedOutAt ? "admin-table-time-active" : ""}>
                      {executive.checkedOutAt
                        ? formatTime(executive.checkedOutAt)
                        : t("admin.dashboard.activeNow")}
                    </span>
                  </div>
                </td>
                <td>
                  {statusKey === "active" ? (
                    <span className="admin-checkout-reason-empty">{t("admin.dashboard.na")}</span>
                  ) : checkoutReasonKey ? (
                    <span className={`admin-checkout-reason-badge is-${checkoutReasonKey}`}>
                      {checkoutReasonKey === "break"
                        ? t("admin.sessions.checkoutReason.break")
                        : checkoutReasonKey === "auto_checkout"
                          ? t("admin.sessions.checkoutReason.autoCheckout")
                          : t("admin.sessions.checkoutReason.offDuty")}
                    </span>
                  ) : (
                    <span className="admin-checkout-reason-empty">
                      {t("admin.sessions.checkoutReason.notSet")}
                    </span>
                  )}
                </td>
              </tr>
            );
          }}
          title={t("admin.dashboard.todayPromoterActivity")}
          subtitle={`• ${t("admin.dashboard.liveUpdates")}`}
          showHeader
          showSearch={false}
          loading={loading}
          tableClassName="admin-table admin-dashboard-activity-table"
          containerClassName="admin-users-section admin-dashboard-activity-table-container"
          initialItemsPerPage={5}
          itemsPerPageOptions={[5, 10, 20, 50]}
          itemsPerPagePlacement="top"
          defaultSortKey="checkedInAt"
          defaultSortDirection="desc"
          emptyMessage={t("admin.dashboard.noPromoterActivity")}
        />
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, bg }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-icon" style={{ backgroundColor: bg }}>
        {icon}
      </div>
      <div className="admin-stat-info">
        <div className="admin-stat-label">{label}</div>
        <div className="admin-stat-value">{value}</div>
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="admin-stat-card">
      <div className="dashboard-skeleton-icon"></div>
      <div className="admin-stat-info">
        <div className="dashboard-skeleton-text" style={{ width: '100px', height: '12px', marginBottom: '12px' }}></div>
        <div className="dashboard-skeleton-text" style={{ width: '140px', height: '28px' }}></div>
      </div>
    </div>
  );
}
